import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import {
    MICROSOFT_LOGIN_ATTEMPT_KEY,
    MicrosoftAuthService,
} from '../services/microsoft-auth.service';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {
    constructor(
        private router: Router,
        private authService: AuthService,
        private microsoftAuthService: MicrosoftAuthService
    ) {}

    async canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean> {
        try {
            const href = typeof window !== 'undefined' ? window.location.href : '';
            const hasToken = this.authService.hasValidAccessToken();
            console.debug('[AuthGuard] Checking authentication', {
                url: href,
                hasToken,
            });
            const loginAttempt =
                sessionStorage.getItem(MICROSOFT_LOGIN_ATTEMPT_KEY) === '1';
            console.debug('[AuthGuard] Auth redirect state', {
                loginAttempt,
            });

            if (loginAttempt) {
                const result = await this.microsoftAuthService.handleRedirect({
                    allowSilentFallback: true,
                });
                if (result?.accessToken) {
                    sessionStorage.removeItem(MICROSOFT_LOGIN_ATTEMPT_KEY);
                    const success = await this.authService.loginWithMicrosoft(
                        result.accessToken
                    );
                    if (success) {
                        console.debug(
                            '[AuthGuard] Microsoft redirect successful, access granted'
                        );
                        return true;
                    }
                } else {
                    sessionStorage.removeItem(MICROSOFT_LOGIN_ATTEMPT_KEY);
                    console.debug('[AuthGuard] Redirect attempt finished without access token');
                }
            }

            const isLoggedIn = await this.authService.ensureUserLoaded();
            if (isLoggedIn) {
                return true;
            }
        } catch (error) {
            console.error('[AuthGuard] Microsoft login handling failed', error);
        }

        if (state.url !== '/login') {
            this.router.navigate(['login']);
        }
        console.debug('[AuthGuard] Guard denied access, redirecting to login');
        return false;
    }
}
